import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { mountAvatar } from '@/lib/codebaby';
import type {
  AvatarSession,
  CoachMeta,
  CBPersonaSignals,
  CBResponse,
  CBStartData,
  MountOptions,
  StartData,
} from '@/lib/codebaby';
import { neutralClientData } from '@/lib/codebaby';
import './AvatarMount.css';

/**
 * Declarative wrapper around the imperative CodeBaby loader API.
 *
 * Ported from the Comcast Sales Academy. Comcast's original version
 * expected a subscribe-pattern AvatarSession (`session.onResponse(fn)`).
 * Our live adapter uses callbacks-in-options
 * (`mountAvatar({ onResponse })`), so this wrapper bridges by exposing
 * the onResponse / onMicrophoneData / etc. handlers as React props and
 * passing them straight through to mountAvatar.
 *
 * Meta-classification streams (`onMeta`, `onPersonaSignals`) are
 * accepted as props for Comcast-shape API symmetry but the shim never
 * fires them — per ADR 0002, classifier dispatch happens host-side
 * (see src/lib/classifier/dispatch.ts), not as a side effect of the
 * avatar loader. Hosts that want classifier signals call
 * `runClassifierDispatch` directly from their onResponse handler.
 *
 * The canvas parking / rehoming pattern is preserved from Comcast:
 * on unmount, the canvas is stashed in an off-screen `#cb-avatar-
 * parking` node; on next mount of another AvatarMount, it's
 * relocated into the new container. Lets us navigate Dashboard →
 * Lesson → Debrief without re-initializing the loader (which the
 * MLS template doesn't tolerate).
 */

export interface AvatarHandle {
  ask(text: string): void;
  playResponse(text: string): void;
  microphoneToggle(): void;
  /** Silence audio; keeps the segment running. Use `pause()` to actually stop. */
  mute(): void;
  unmute(): void;
  /** Stop the current segment (audio + animation). Fires segmentEnded. */
  pause(): void;
  /** Resume a paused segment by re-emitting the last response. */
  play(): void;
  resetConversation(): void;
  notifyContext(payload: { type: string; description: string; url?: string }): void;
}

export interface AvatarMountProps {
  loaderId: string;
  /** Mount-time context for the loader. Ported screens pass the
   *  Comcast-shape CBStartData (discriminated by mode); we pass it
   *  to mountAvatar through a cast — at runtime startData is just
   *  a JSON-serializable blob shoved into the loader URL. */
  startData: CBStartData;
  /** Passed-through className on the container div. */
  className?: string;
  /** Fires once the avatar is loaded and ready to accept asks. */
  onPlayerBuilt?: () => void;
  /** Fires on every avatar response. The shim wraps our adapter's
   *  ResponsePayload ({ text, audioUrl }) in a CBResponse envelope —
   *  clientData defaults to neutral. Hosts that want real classifier
   *  signals call `runClassifierDispatch` from inside this handler
   *  (see src/lib/classifier/dispatch.ts). */
  onResponse?: (r: CBResponse) => void;
  /** Fires on interim and final mic transcriptions. */
  onMicrophoneData?: (d: { text: string; isFinal: boolean }) => void;
  /** Fires when the current avatar audio segment finishes. */
  onSegmentEnded?: () => void;
  /** Coach-side classifier metadata. Accepted for Comcast-shape API
   *  symmetry but the shim never fires it — hosts dispatch classifier
   *  signals themselves (see src/lib/classifier/dispatch.ts). Reserved
   *  for a future architecture where Coach prompts ship to the
   *  classifier the same way persona prompts do today. */
  onMeta?: (m: CoachMeta) => void;
  /** Persona-side scoring signals. Same story — kept for shape
   *  symmetry, but the shim never fires it. */
  onPersonaSignals?: (s: CBPersonaSignals) => void;
  /** Fires when the loader's mic transitions to actively-listening. */
  onMicrophoneListening?: () => void;
  /** Fires when the loader closes the mic. */
  onMicrophoneStopped?: () => void;
  /** Extra aria-label for the mounted stage. */
  ariaLabel?: string;
}

const AvatarMount = forwardRef<AvatarHandle, AvatarMountProps>(function AvatarMount(props, ref) {
  const sessionRef = useRef<AvatarSession | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Keep latest handlers in refs so identity changes don't re-mount
  // the avatar (which is not safe to re-initialize).
  const handlersRef = useRef(props);
  handlersRef.current = props;

  // Onsubscribed-via-our-callbacks handlers — stable references that
  // dispatch to whatever's currently in handlersRef.
  const callbacks = useRef<
    Pick<
      MountOptions,
      | 'onPlayerBuilt'
      | 'onResponse'
      | 'onInterimResponse'
      | 'onMicrophoneStarted'
      | 'onMicrophoneReady'
      | 'onMicrophoneStopped'
      | 'onMicrophoneData'
      | 'onSegmentEnded'
      | 'onError'
    >
  >({
    onPlayerBuilt: () => handlersRef.current.onPlayerBuilt?.(),
    onResponse: (payload) =>
      handlersRef.current.onResponse?.({
        answerText: payload.text,
        clientData: neutralClientData(),
      }),
    onMicrophoneReady: () => handlersRef.current.onMicrophoneListening?.(),
    onMicrophoneStopped: () => handlersRef.current.onMicrophoneStopped?.(),
    onMicrophoneData: (d) => handlersRef.current.onMicrophoneData?.(d),
    onSegmentEnded: () => handlersRef.current.onSegmentEnded?.(),
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Rehome any canvas that was parked from a previous AvatarMount.
    rehomeTo(container);

    // Watch the parking node for late arrivals (React 18 can mount
    // this component before the outgoing AvatarMount has parked its
    // canvas — the initial rehome then finds an empty parking node).
    const parking = getParkingNode();
    const parkingObserver = new MutationObserver(() => rehomeTo(container));
    parkingObserver.observe(parking, { childList: true });

    const result = mountAvatar({
      containerEl: container,
      avatarLoaderId: props.loaderId,
      // CBStartData (Comcast shape) is JSON-serializable and shoved
      // into the loader URL by the live adapter. Cast to our older
      // StartData so the mount call typechecks; runtime behavior is
      // identical (both pass through JSON.stringify).
      startData: props.startData as unknown as StartData,
      ...callbacks.current,
    });
    sessionRef.current = result.session;

    // Babylon binds its WebGL rendertarget to the container's size at
    // the moment the canvas lands. On a client-side route change the
    // container is often still 0-sized (route transition not painted
    // yet) or the canvas was rehomed from parking without a fresh
    // relocation — either way Babylon binds at 0x0 and renders a black
    // box even though audio plays. A hard reload avoids this because the
    // container is full-size from first paint. Fix: nudge a window
    // 'resize' (which Babylon listens for) on the next few frames AND
    // whenever the container actually changes size, so the canvas
    // re-measures once the layout settles. See bb6b5fc / the WAITING
    // black-box report.
    const nudgeResize = () => {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('resize'));
    };
    const rafIds: number[] = [];
    if (typeof requestAnimationFrame === 'function') {
      // A short cascade: catch the frame the container gets laid out on.
      [0, 1, 2, 8].forEach((delay) => {
        const schedule = (n: number, fn: () => void) =>
          n <= 0 ? fn() : requestAnimationFrame(() => schedule(n - 1, fn));
        schedule(delay, () => rafIds.push(requestAnimationFrame(nudgeResize)));
      });
    }
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      let lastW = 0;
      let lastH = 0;
      resizeObserver = new ResizeObserver((entries) => {
        const box = entries[0]?.contentRect;
        if (!box) return;
        // Only nudge on a real dimension change (esp. the 0 -> non-zero
        // transition that the black box is stuck on). Avoids a feedback
        // loop with Babylon's own resize handling.
        if (Math.round(box.width) !== lastW || Math.round(box.height) !== lastH) {
          lastW = Math.round(box.width);
          lastH = Math.round(box.height);
          if (lastW > 0 && lastH > 0) nudgeResize();
        }
      });
      resizeObserver.observe(container);
    }

    return () => {
      parkingObserver.disconnect();
      resizeObserver?.disconnect();
      rafIds.forEach((id) => cancelAnimationFrame(id));
      // Park canvas + canvas-adjacent children; do NOT call
      // session.unmount() — the loader doesn't tolerate re-init.
      parkChildrenOf(container);
      sessionRef.current?.unmount();
      sessionRef.current = null;
    };
    // Intentional: re-mount only when loaderId or startData identity
    // changes. Parents should memoize startData if they don't want
    // remounts on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.loaderId, props.startData]);

  useImperativeHandle(
    ref,
    (): AvatarHandle => ({
      ask: (text) => sessionRef.current?.ask(text),
      playResponse: (text) => sessionRef.current?.playResponse(text),
      microphoneToggle: () => sessionRef.current?.toggleMicrophone(),
      mute: () => sessionRef.current?.mute(),
      unmute: () => sessionRef.current?.unmute(),
      // Our live adapter doesn't expose pause/play (the loader's
      // primitive is na-stop, which we surface as resetConversation).
      // Mapping pause → mute for now; Chunk 5 may add a proper
      // pause/resume primitive if the loader supports it.
      pause: () => sessionRef.current?.mute(),
      play: () => sessionRef.current?.unmute(),
      resetConversation: () => sessionRef.current?.resetConversation(),
      notifyContext: (payload) => sessionRef.current?.pushContext(payload),
    }),
    [],
  );

  return (
    <div
      ref={containerRef}
      id="avatarContainer"
      className={`cb-mount ${props.className ?? ''}`}
      aria-label={props.ariaLabel}
    />
  );
});

export default AvatarMount;

/* --------------------- Canvas parking across routes --------------------- */

const PARK_ID = 'cb-avatar-parking';

function getParkingNode(): HTMLElement {
  let parking = document.getElementById(PARK_ID);
  if (!parking) {
    parking = document.createElement('div');
    parking.id = PARK_ID;
    parking.setAttribute('aria-hidden', 'true');
    parking.style.cssText =
      'position:fixed;left:-99999px;top:-99999px;width:1px;height:1px;' +
      'overflow:hidden;transform:translate(0,0);pointer-events:none;';
    document.body.appendChild(parking);
  }
  return parking;
}

function parkChildrenOf(container: HTMLElement) {
  if (container.childElementCount === 0) return;
  const parking = getParkingNode();
  while (container.firstChild) {
    parking.appendChild(container.firstChild);
  }
}

function rehomeTo(container: HTMLElement) {
  const parking = document.getElementById(PARK_ID);
  if (!parking) return;
  let moved = false;
  while (parking.firstChild) {
    container.appendChild(parking.firstChild);
    moved = true;
  }
  if (moved) {
    window.dispatchEvent(new Event('resize'));
  }
}
